import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import SpeedTestEngine from '@cloudflare/speedtest';

interface SpeedTestResults {
  downloadSpeed: number;
  uploadSpeed: number;
  ping: number;
}


@Component({
  standalone: true,
  selector: 'app-speed-test',
  templateUrl: './speed-test.component.html',
  styleUrls: ['./speed-test.component.scss'],
  imports: [MatDialogModule, CommonModule]
})
export class SpeedTestComponent implements OnInit {

  isRunning: boolean = false;
  results: SpeedTestResults | null = null;
  error: string | null = null;
  private testTimeout: any = null;

  constructor(
    public dialogRef: MatDialogRef<SpeedTestComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    // Auto-start the speed test when opened in dialog
    this.startSpeedTest();
  }

  startSpeedTest(): void {
    this.isRunning = true;
    this.results = null;
    this.error = null;

    // Clear any existing timeout
    if (this.testTimeout) {
      clearTimeout(this.testTimeout);
    }

    // Set a maximum timeout of 90 seconds
    this.testTimeout = setTimeout(() => {
      if (this.isRunning && !this.results) {
        this.error = 'Speed test timed out. Please try again.';
        this.isRunning = false;
      }
    }, 90000); // 90 seconds

    this.runSpeedTest();
  }

  private async runSpeedTest(): Promise<void> {
    try {
      // Use Cloudflare Speedtest library for accurate measurements
      const speedTest = new SpeedTestEngine({
        autoStart: true
      });
      
      // Wait for test to complete
      await new Promise<void>((resolve, reject) => {
        speedTest.onFinish = (results: any) => {
          const summary = results.getSummary();
          
          // Clear the timeout since we got results
          if (this.testTimeout) {
            clearTimeout(this.testTimeout);
          }

          this.isRunning = false;
          this.error = null;
          
          // Convert bytes to Mbps (multiply by 8 to get bits, divide by 1,000,000 to get Mbps)
          const downloadMbps = summary.download ? summary.download / 1000000 : 0;
          const uploadMbps = summary.upload ? summary.upload / 1000000 : 0;
          
          this.results = {
            downloadSpeed: Math.round(downloadMbps * 100) / 100,
            uploadSpeed: Math.round(uploadMbps * 100) / 100,
            ping: summary.latency || 0
          };
          
          console.log('Speed test completed:', this.results);
          resolve();
        };
        
        speedTest.onError = (error: string) => {
          // Only show error if test is actually finished
          if (speedTest.isFinished) {
            reject(error);
          }
        };
      });
      
    } catch (error) {
      this.handleSpeedTestError(error);
    }
  }

  private handleSpeedTestError(error: any): void {
    const errorMessage = typeof error === 'string' ? error : 'Speed test failed.';
    
    console.log('Speed test error received:', errorMessage);
    
    // Simple error handling - just wait a bit and see if results come through
    setTimeout(() => {
      if (!this.results && this.isRunning) {
        this.error = 'Speed test failed. Please try again.';
        this.isRunning = false;
      }
    }, 3000);
  }

}
